import { Controller } from '@hotwired/stimulus'
import { loadScript } from '@paypal/paypal-js'
import { destroy, post } from '@rails/request.js'

const PAYPAL_CREATE_ORDER_PATH = '/payments/paypal/orders'
const PAYPAL_PAY_ORDER_PATH = '/payments/paypal/pay'
const PURCHASE_WINDOW = 300000 // miliseconds (5 minutes)
const USER_HOMEPAGE_URL = '/user_account/home'

/**
 * This Class integrates Paypal for checkout functionality.
 * It depends on '/payments/paypal/orders' and '/payments/paypal/pay' paths to create and
 * pay the order in the server. The steps to complete the checkout are:
 *  1. User clicks the PayPal button. When this occurs it will send a request to the server to create
 *     a confirmed order
 *  2. Once the confirmed order is created the user must pay for it using the Paypal Popup.
 *  3. Once the user pays for the order a request is sent to the server to mark the order as paid.
 */
export default class PaypalCheckoutController extends Controller {
  static values = { clientId: String, enabled: Boolean, menuToken: String }
  static targets = [
    'paypalOrderId',
    'paypalOrderTotalPaid',
    'paypalPayerCountryCode',
    'button',
    'error'
  ]

  connect = async () => {
    window.addEventListener('beforeunload', () => { this.removeOrder() })

    this.orderToken = null
    this.orderTotal = 0

    this.paypal = await loadScript({
      locale: 'es_MX',
      currency: 'MXN',
      clientId: this.clientIdValue
    })

    this.renderPayPalButton()
  }

  /**
   * Renders PayPal button
   */
  renderPayPalButton = () => {
    this.paypal
      .Buttons({
        style: { shape: 'pill' },
        fundingSource: this.paypal.FUNDING.PAYPAL,
        createOrder: this.createOrder,
        onApprove: this.onApprove,
        onError: this.onError,
        onInit: this.onInit,
        onClick: this.onClick,
        onCancel: this.onCancel
      })
      .render(this.buttonTarget)
  }

  /**
   * Creates a Paypal order
   * @param {Object} _  Unused parameter
   * @param {Object} actions Actions object provided by PayPal.
   * @returns {Promise} A promise that resolves to the created order.
   */
  createOrder = (_, actions) => {
    this.errorTarget.classList.add('hidden')

    return actions.order.create({
      purchase_units: [{ custom_id: this.orderToken, amount: { value: this.orderTotal } }]
    })
  }

  /**
   * Disables actions of PayPal button if the controller is not enabled.
   * @param {Object} _ Unused parameter
   * @param {Object} actions Actions object provided by PayPal.
   */
  onInit = (_, actions) => {
    if (!this.enabledValue) { actions.disable() }
  }

  /**
   *  Handles the onApprove event when a PayPal order is approved.
   *  It sends a request to the server to pay the order.
   *  The server must return a redirection to the confirmed order page.
   * @param {Object} _ Unused parameter
   * @param {Object} actions Actions object provided by PayPal.
   * @returns {Promise} A promise that resolves after processing the PayPal order capture.
   */
  onApprove = (_, actions) => {
    return actions.order.capture().then(details => {
      this.activateButtonLoader()
      const paidByUser = this.getTotalPaid(details.purchase_units)

      this.paypalOrderIdTarget.value = details.id
      this.paypalOrderTotalPaidTarget.value = paidByUser
      this.paypalPayerCountryCodeTarget.value = details.payer.address.country_code

      const body = {
        token: this.orderToken,
        payment_details: {
          paypal_order_id: details.id,
          paypal_paid_by_user: paidByUser,
          paypal_payer_country_code: details.payer.address.country_code,
          paypal_transaction_id: details.purchase_units[0].payments.captures[0].id
        }
      }

      return post(PAYPAL_PAY_ORDER_PATH, { body }).then(async response => {
        if (!response.redirected) return

        window.Turbo.session.visit(response.response.url)
      }).catch((err) => {
        window.Sentry.captureException(err)
        window.Turbo.session.visit(USER_HOMEPAGE_URL)
      })
    }).catch((err) => {
      window.Sentry.captureException(err)
      window.Turbo.session.visit(USER_HOMEPAGE_URL)
    })
  }

  /**
   * Handles errors that occur during the PayPal flow.
   * @param {Error} err The error object representing the encountered error.
   */
  onError = err => {
    this.errorTarget.classList.remove('hidden')
    window.Sentry.captureException(err)
    this.removeOrder()
  }

  /**
   * Handles the onClick event when the PayPal button is clicked.
   * It sends a request to the server to create a confirmed order.
   * This method handles three types of requests:
   *  1. TurboStream. A TurboStream response is returned by the server whe it fails to create the order
   *  2. Redirection. A Redirection response is returned by the server whe it fails to create the order
   *  3. Json Response. A json response is returned by the server when it creates the order succesfully
   * @param {Object} _ Unused parameter
   * @param {Object} actions Actions object provided by PayPal.
   * @returns {Promise} A promise that resolves after processing the PayPal order creation response.
   */
  onClick = async (_, actions) => {
    const response = await post(PAYPAL_CREATE_ORDER_PATH)

    if (response.statusCode === 500) return actions.reject()
    if (response.isTurboStream) {
      response.renderTurboStream()
      return actions.reject()
    } else if (response.redirected) {
      window.Turbo.session.visit(response.response.url)
      return actions.reject()
    }

    const { total: orderTotal, token: orderToken } = await response.json

    this.orderToken = orderToken
    this.orderTotal = orderTotal

    setTimeout(() => {
      this.paypal.Buttons.instances.forEach(instance => {
        this.removeOrder()
        instance.close()
        this.renderPayPalButton()
      })
    }, PURCHASE_WINDOW)

    return actions.resolve()
  }

  /**
   * Handles onCancel event when the PayPal checkout is canceled
   * @param {Object} data Data object provided by PayPal
   * @param {Object} actions Actions object provided by PayPal
   */
  onCancel = (data, actions) => {
    this.removeOrder()
  }

  /**
   * Calculates the total amount paid for the purchase units.
   * @param {Array} purchaseUnits  An array of purchase units containing payment information.
   * @returns {number} The total amount paid for the purchase units.
   */
  getTotalPaid = purchaseUnits => {
    return purchaseUnits.reduce((total, purchaseUnit) => {
      return total + purchaseUnit.payments.captures.reduce((acc, e) => {
        return acc + e.amount.value
      }, 0)
    }, 0)
  }

  /**
   * Removes the order in the server if it exists.
   * @returns {Promise} A promise representing the destruction of the order.
   */
  removeOrder = () => {
    if (!this.orderToken) return

    return destroy(PAYPAL_CREATE_ORDER_PATH, { body: { token: this.orderToken } })
  }

  activateButtonLoader = () => {
    this.element.querySelector('.paypal-buttons').classList.add('button', 'btn-loading')
  }
}
